import React, { createContext, useContext, useState, useCallback } from "react";
import * as api from "../services/api";

const RoomContext = createContext();

export const RoomProvider = ({ children }) => {
  const [roomId, setRoomId] = useState(null);
  const [roomData, setRoomData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createRoom = useCallback(async (hotelId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.createRoom(hotelId);
      const id = response.data?.roomId;
      setRoomId(id);
      setRoomData(response.data || {});
      return id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoom = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getRoom(id);
      setRoomId(id);
      setRoomData(response.data?.data || response.data || {});
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getPetBasicInfo = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getPetBasicInfo(id);
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHotelRooms = useCallback(async (id, status = null) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getHotelRooms(id, status);
      setRoomId(id);
      setRoomData(response.data?.data || response.data || {});
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateRoomStep = useCallback(
    async (id, stepKey, stepData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.updateRoomStep(id, stepKey, stepData);
        setRoomData((prev) => ({
          ...prev,
          [stepKey]: stepData[stepKey],
        }));
        return response.data?.data || response.data;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateRoomStatus = useCallback(async (id, status) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.updateRoomStatus(id, status);
      setRoomData((prev) => ({
        ...prev,
        status: status,
      }));
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const duplicateRoom = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.duplicateRoom(id);
      return response.data?.roomId;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteRoom = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.deleteRoom(id);
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createPublishedRooms = useCallback(async (roomId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.createPublishedRooms(roomId);
      return response.data?.publishedRooms || response.data?.data || [];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPublishedRooms = useCallback(async (hotelId, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getPublishedRooms(hotelId, filters);
      return response.data || [];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePublishedRooms = useCallback(async (roomId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.deletePublishedRooms(roomId);
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const ListRoomBookings = useCallback(async (roomId, params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.ListRoomBookings(roomId, params);
      return response.data || [];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateBookingStep = useCallback(async (bookingId, stepKey, stepData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.updateBookingStep(bookingId, stepKey, stepData);
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePublishedRoomInfo = useCallback(async (roomId, publishedRoomId, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.updatePublishedRoomInfo(roomId, publishedRoomId, data);
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReviewDocument = useCallback(async (bookingId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getReviewDocument(bookingId);
      return response.data?.data || response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    roomId,
    roomData,
    loading,
    error,
    createRoom,
    fetchRoom,
    updateRoomStep,
    updateRoomStatus,
    duplicateRoom,
    deleteRoom,
    fetchHotelRooms,
    createPublishedRooms,
    fetchPublishedRooms,
    deletePublishedRooms,
    ListRoomBookings,
    getPetBasicInfo,
    updateBookingStep,
    fetchReviewDocument,
    updatePublishedRoomInfo,
  };

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
};

export const useRoom = () => {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error("useRoom must be used within RoomProvider");
  }
  return context;
};
